let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;

  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    localStorage.setItem('access_token', data.access);
    return true;
  } catch {
    return false;
  }
};

export const apiFetch = async (url: string, options: RequestInit = {}): Promise<any> => {
  const makeRequest = async () => {
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, { ...options, headers });
  };

  let response = await makeRequest();

  // If 401, try to refresh the token
  if (response.status === 401) {
    // Prevent multiple simultaneous refresh attempts
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken();
    }

    const refreshed = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (refreshed) {
      // Retry the original request with new token
      response = await makeRequest();
    } else {
      // Refresh failed, clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login?message=session_expired';
      throw new Error('Session expired');
    }
  }

  if (!response.ok) {
    let errorData: any = null;
    try {
      errorData = await response.json();
    } catch (err) {
      throw new Error(response.statusText);
    }

    // Build a user-friendly message
    let message = errorData.detail || errorData.error;
    if (!message && typeof errorData === 'object') {
      message = Object.entries(errorData)
        .map(([field, errs]) => {
          const joined = Array.isArray(errs) ? errs.join(' ') : `${errs}`;
          return `${field}: ${joined}`;
        })
        .join(' | ');
    }

    throw new Error(message || 'API request failed');
  }

  // Handle empty responses (e.g., 204 No Content from DELETE)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }

  return response.json();
};